import React, { useEffect, useMemo, useState } from "react";
import { Badge, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Constants from "../../constants";
import HttpManager from "../../services/HttpManager";
import EnrollmentsService from "../../services/campus/enrollments.service";
import { getErrorMessage } from "../../common";
import { AdvisorSelection, AvailableScheduleDTO } from "../../models/EnrollmentHistoryDTO";
import AdvisorsPicker from "../../components/enrollment/advisorsPicker";
import SchedulePicker from "../../components/enrollment/schedulePicker";
import { formatLocalDateTime } from "../../components/enrollment/format";
import { sinAcentos, cursoCorto } from "../../utils/cursoCorto";
import { horarioBonito } from "../../utils/horarioBonito";
import { ahoraCdmx } from "../../utils/horaMexico";

export interface EnrollStudentModalProps {
	studentId: number;
	studentName: string;
	campusId: number;
	onClose: () => void;
	onEnrolled: () => void;
}

/**
 * Enroll a student from their profile: pick one of the schedules WAPP allows for them (sign-up rules),
 * the advisors and a sign-up note (kits are never touched here). Uses the same server endpoint as the Horarios screen,
 * so every validation (overdue balance, duplicate module, hour clash) still applies.
 */
// Día de la semana que aparece en el nombre o las sesiones del horario, para
// que dentro de una misma fecha los horarios salgan de lunes a domingo.
const DIAS = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
const ABREVIATURAS = ["lu", "ma", "mi", "ju", "vi", "sa", "do"];

const rangoDia = (texto: string): number => {
	const t = sinAcentos(texto);
	for (let i = 0; i < DIAS.length; i++) {
		if (t.includes(DIAS[i])) {
			return i;
		}
	}
	const abreviatura = t.match(/\b(lu|ma|mi|ju|vi|sa|do)\b/);
	return abreviatura ? ABREVIATURAS.indexOf(abreviatura[1]) : 99;
};

const soloFecha = (valor: string | null | undefined): string => String(valor || "").slice(0, 10);

const comoTexto = (fecha: Date): string => [
	fecha.getFullYear(),
	String(fecha.getMonth() + 1).padStart(2, "0"),
	String(fecha.getDate()).padStart(2, "0")
].join("-");

/** Se matricula gente hasta antes de la segunda clase, así que lo que arrancó esta última semana
 *  sigue contando como "por comenzar" (Esteban, 2026-09-11). */
const DIAS_DE_GRACIA = 7;

/**
 * Orden de los horarios, en tres grupos (Esteban, 2026-09-24):
 *   0. Los que arrancaron en los últimos 7 días: se matricula hasta antes de la segunda clase.
 *   1. Los que están por comenzar, del más próximo al más lejano.
 *   2. Los que ya llevan tiempo corriendo (reingresos a grupos de periodos anteriores), del más
 *      reciente hacia atrás.
 * La tabla pone un título a cada grupo: sin él, "Ene 2027" (último por comenzar) quedaba pegado a
 * "Ago 2026" (primero de los ya comenzados) y parecía fuera de orden.
 * Dentro de una misma fecha se agrupa por curso, módulo y día de la semana.
 */
export const GRUPOS_HORARIO = ["Comenzaron esta semana", "Por comenzar", "Ya comenzaron"];

export const grupoHorario = (schedule: AvailableScheduleDTO): number => {
	const hoy = comoTexto(ahoraCdmx());
	const desde = ahoraCdmx();
	desde.setDate(desde.getDate() - DIAS_DE_GRACIA);
	const fecha = soloFecha(schedule.startDate);
	if (fecha > hoy) {
		return 1;
	}
	return fecha >= comoTexto(desde) ? 0 : 2;
};

export const ordenarHorarios = (horarios: AvailableScheduleDTO[]): AvailableScheduleDTO[] => {
	return [...horarios].sort((a, b) => {
		const grupoA = grupoHorario(a);
		const grupoB = grupoHorario(b);
		if (grupoA !== grupoB) {
			return grupoA - grupoB;
		}
		const fechaA = soloFecha(a.startDate);
		const fechaB = soloFecha(b.startDate);
		if (fechaA !== fechaB) {
			// Esta semana y por comenzar: la fecha más temprana primero. Ya comenzados: el más reciente primero.
			return grupoA === 2 ? (fechaA > fechaB ? -1 : 1) : (fechaA < fechaB ? -1 : 1);
		}
		return (a.courseName || "").localeCompare(b.courseName || "", "es")
			|| (a.levelName || "").localeCompare(b.levelName || "", "es")
			|| (rangoDia(`${a.name} ${a.sessions}`) - rangoDia(`${b.name} ${b.sessions}`))
			|| (a.name || "").localeCompare(b.name || "", "es");
	});
};

const MESES_CORTOS: {[largo: string]: string} = {
	enero: "Ene", febrero: "Feb", marzo: "Mar", abril: "Abr",
	mayo: "May", junio: "Jun", julio: "Jul", agosto: "Ago",
	septiembre: "Sep", octubre: "Oct", noviembre: "Nov", diciembre: "Dic"
};

/** "Septiembre 2026" -> "Sep 2026" */
export const periodoCorto = (nombre: string): string =>
	String(nombre || "").replace(/[A-Za-zÁÉÍÓÚÑáéíóúñ]+/g, (palabra) =>
		MESES_CORTOS[sinAcentos(palabra)] ?? palabra);

/** "Nelson Salvador" + "Castro Rodríguez" -> "Nelson Castro" */
export const maestroCorto = (schedule: AvailableScheduleDTO): string =>
	[schedule.mentorFirstName, schedule.mentorLastName]
		.map((parte) => String(parte || "").trim().split(/\s+/)[0] || "")
		.filter(Boolean)
		.join(" ") || String(schedule.mentorName || "").trim();

/*
	Solo se ofrecen los horarios donde de verdad se puede matricular a alguien:
	activos y que no hayan terminado. Los que ya acabaron seguian saliendo en la
	lista y no sirven para nada aqui. Uno por comenzar con cero alumnos si sale:
	para eso esta la matricula. (Esteban, 2026-09-16)
*/
export const seLePuedeMatricular = (schedule: AvailableScheduleDTO): boolean => {
	if (String(schedule.status || "").toLowerCase() !== "activo") {
		return false;
	}

	const fin = String(schedule.endDate || "").slice(0, 10);

	if (!fin) {
		return true;
	}

	const hoy = ahoraCdmx();
	const hoyTexto = hoy.getFullYear() + "-"
		+ String(hoy.getMonth() + 1).padStart(2, "0") + "-"
		+ String(hoy.getDate()).padStart(2, "0");

	return fin >= hoyTexto;
};

const EnrollStudentModal: React.FC<EnrollStudentModalProps> = ({ studentId, studentName, campusId, onClose, onEnrolled }) => {
	const http = HttpManager.getInstance();
	const [schedules, setSchedules] = useState<AvailableScheduleDTO[] | null>(null);
	const [filter, setFilter] = useState<string>("");
	//Se puede matricular en varios horarios de una vez, cada uno con su propia nota (Esteban, 2026-09-22).
	const [picks, setPicks] = useState<{ schedule: AvailableScheduleDTO; notes: string }[]>([]);
	//null = todavía no eligen asesoras; [] = eligieron "Ninguno" (Esteban, 2026-09-11).
	const [advisors, setAdvisors] = useState<AdvisorSelection[] | null>(null);
	const [intentoGuardar, setIntentoGuardar] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<boolean>(false);

	useEffect(() => {
		http.scheduleService.getAvailableSchedules(campusId, studentId)
			.then((response) => setSchedules(response.data))
			.catch((e) => { setSchedules([]); setError(getErrorMessage(e)); });
	}, [campusId, studentId]);

	const isPicked = (id: number) => picks.some((pick) => pick.schedule.id === id);

	const select = (schedule: AvailableScheduleDTO) => {
		setError(null);
		setPicks((list) => list.some((pick) => pick.schedule.id === schedule.id)
			? list.filter((pick) => pick.schedule.id !== schedule.id)
			: [...list, { schedule, notes: "" }]);
	};

	const setPickNotes = (id: number, value: string) =>
		setPicks((list) => list.map((pick) => pick.schedule.id === id ? { ...pick, notes: value } : pick));

	const faltanObligatorios: boolean = advisors === null || picks.some((pick) => pick.notes.trim() === "");

	const enroll = async () => {
		if (picks.length < 1) {
			setError(Constants.Common.selectScheduleFirst);
			return;
		}
		//Asesoras y nota son obligatorias; "Ninguno" es una respuesta válida (Esteban, 2026-09-11).
		if (faltanObligatorios) {
			setIntentoGuardar(true);
			setError(advisors === null ? Constants.Common.advisorsRequired : Constants.Common.signupNoteRequired);
			return;
		}
		//Matricular no se deshace solo: se confirma antes, con los cursos a la vista (Esteban, 2026-09-22).
		const resumen = picks.map((pick) => "· " + cursoCorto(pick.schedule.courseName) + " — " + horarioBonito(pick.schedule.name, { plural: true, separador: " " })).join("\n");
		if (!window.confirm("Se va a matricular a " + studentName + " en:\n\n" + resumen + "\n\n¿Confirmas?")) {
			return;
		}
		setBusy(true);
		setError(null);
		try {
			for (const pick of picks) {
				const result = await EnrollmentsService.addEnrollments(
					{ course_id: pick.schedule.courseId, level_id: pick.schedule.levelId, id: pick.schedule.id },
					{
						studentWithKitIds: String(studentId), // kits are NOT assigned here (Esteban, 2026-09-05); Horarios keeps its own kit flow
						notes: pick.notes.trim(),
						advisors: (advisors ?? []).map((advisor) => ({ userId: advisor.userId, sharePercentage: advisor.sharePercentage }))
					}
				);
				if (result.status !== 200 && result.status !== 204) {
					throw new Error(result.statusText);
				}
			}
			setSuccess(true);
			setTimeout(onEnrolled, 900);
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal show onHide={busy ? undefined : onClose} size="xl" centered scrollable>
			<Modal.Header closeButton={!busy}>
				<Modal.Title style={{ fontSize: 18 }}>
					{Constants.Common.enrollStudent}: <b>{studentName}</b>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left" }}>
				{error ? <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div> : ""}
				{success ? <div className="alert alert-success" style={{ fontSize: 13 }}>{Constants.Common.enrollSuccess}</div> : ""}
				<SchedulePicker schedules={schedules} picked={picks.map((pick) => pick.schedule.id)} onToggle={select}
					filter={filter} onFilter={setFilter} disabled={busy} />
				{picks.length > 0 ? (
					<div className="row" style={{ marginTop: 14, rowGap: 10 }}>
						<div className="col-md-6">
							<AdvisorsPicker campusId={campusId} value={advisors} onChange={setAdvisors} disabled={busy} invalid={intentoGuardar} />
						</div>
						<div className="col-md-12">
							{picks.map((pick) => (
								<div key={"pn_" + pick.schedule.id} style={{ marginBottom: 10 }}>
									<label htmlFor={"enrollNotes_" + pick.schedule.id} style={{ position: "relative", display: "block" }}>
										{Constants.Common.signupNote} · {cursoCorto(pick.schedule.courseName)} — {horarioBonito(pick.schedule.name, { plural: true, separador: " " })}
										<span style={{ color: "var(--wa-rojo)" }}> *</span>
									</label>
									<textarea id={"enrollNotes_" + pick.schedule.id} className="form-control" style={{ minHeight: 80 }} maxLength={2000} disabled={busy}
										placeholder={Constants.Common.signupNotePlaceholder} value={pick.notes}
										onChange={(event) => setPickNotes(pick.schedule.id, event.target.value)} />
									{intentoGuardar && pick.notes.trim() === ""
										? <div style={{ fontSize: 13, color: "var(--wa-rojo)", marginTop: 4 }}>{Constants.Common.signupNoteRequired}</div>
										: ""}
								</div>
							))}
						</div>
					</div>
				) : ""}
			</Modal.Body>
			<Modal.Footer>
				<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onClose}>{Constants.Common.close}</button>
				{picks.length < 1 || !faltanObligatorios ? "" : <span style={{ fontSize: 13, color: "var(--wa-apagado)", marginRight: "auto" }}>
					{Constants.Common.requiredFieldsHint}
				</span>}
				<button type="button" className="btn btn-primary rounded-pill" disabled={busy || success || picks.length < 1} onClick={enroll}>
					<FontAwesomeIcon icon={solid("user-plus")} />&nbsp;{Constants.Common.enroll}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

export default EnrollStudentModal;
